import type { Card } from '@cardengine/shared';
import { useEffect, useRef, useState } from 'react';
import { useStore } from '../store.js';
import { PlayingCard } from './PlayingCard.js';

interface Flight {
  id: number;
  cards: Card[];
  fromX: number;
  fromY: number;
  fromScale: number;
  toX: number;
  toY: number;
}

const FLIGHT_MS = 620;

/**
 * Animates an opponent's cards travelling from their seat to the discard pile.
 *
 * Without it, cards simply teleport and a table of AI players feels like a log
 * file. Positions are measured from the live DOM at the moment of the event, so
 * this stays correct however the table is laid out or resized.
 */
export function FlyingCards() {
  const lastEvents = useStore((s) => s.lastEvents);
  const myId = useStore((s) => s.game?.yourPlayerId);
  const [flights, setFlights] = useState<Flight[]>([]);
  const seq = useRef(0);

  useEffect(() => {
    const target = document.querySelector('.pile--discard .pile__drop');
    if (!target) return;
    const to = target.getBoundingClientRect();

    const started: Flight[] = [];
    for (const event of lastEvents) {
      if (event.type !== 'cardsPlayed' || event.playerId === myId) continue;

      const seat = document.querySelector(`[data-player-id="${event.playerId}"] .seat__cards`);
      if (!seat) continue;
      const from = seat.getBoundingClientRect();

      started.push({
        id: ++seq.current,
        cards: event.cards,
        fromX: from.left + from.width / 2,
        fromY: from.top + from.height / 2,
        // Opponent cards are drawn much smaller, so grow into place.
        fromScale: from.height > 0 ? Math.max(0.25, from.height / to.height) : 0.4,
        toX: to.left + to.width / 2,
        toY: to.top + to.height / 2,
      });
    }

    if (started.length === 0) return;
    setFlights((current) => [...current, ...started]);

    const ids = new Set(started.map((f) => f.id));
    const timer = setTimeout(() => {
      setFlights((current) => current.filter((f) => !ids.has(f.id)));
    }, FLIGHT_MS);
    return () => clearTimeout(timer);
  }, [lastEvents, myId]);

  if (flights.length === 0) return null;

  return (
    <div className="flights" aria-hidden="true">
      {flights.map((flight) =>
        flight.cards.map((card, index) => (
          <div
            key={`${flight.id}-${card.id}`}
            className="flight"
            style={
              {
                '--from-x': `${flight.fromX}px`,
                '--from-y': `${flight.fromY}px`,
                '--from-scale': flight.fromScale,
                '--to-x': `${flight.toX}px`,
                '--to-y': `${flight.toY}px`,
                animationDelay: `${index * 70}ms`,
              } as React.CSSProperties
            }
          >
            <PlayingCard card={card} />
          </div>
        )),
      )}
    </div>
  );
}
